/**
 * profile-section.tsx — "Thông tin tài khoản và Bảo mật" (Cài đặt cá nhân).
 *
 * Chỉ dựng những gì backend có thật: họ tên + ảnh đại diện (/profile),
 * email & vai trò (chỉ xem), trạng thái trực tuyến (/presence/me), đổi mật khẩu
 * (/auth/change-password).
 * CHƯA có API đổi email, số điện thoại, xác minh hai bước hay đăng nhập bằng
 * bên thứ ba — nên không hiện các mục đó thay vì hiện nút bấm không chạy.
 */
import { useEffect, useRef, useState } from 'react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { PRESENCE_DOT, PRESENCE_OPTIONS, presenceOf } from '@/lib/presence'
import { usePresenceMap, useSetMyPresence } from '@/hooks/use-presence'
import { useAuthStore } from '@/stores/auth-store'
import type { Profile } from '@/hooks/use-settings'
import {
  useProfile,
  useUpdateProfile,
  useUploadAvatar,
  useDeleteAvatar,
} from '@/hooks/use-settings'
import { ROLE_LABELS, avatarSrc } from '@/pages/settings/settings-utils'
import { AccountRow, AccountSection } from './account-ui'
import { ChangePasswordDialog } from './change-password-dialog'
import { TotpSettingsSection } from './totp-settings-section'

const MAX_AVATAR_BYTES = 2 * 1024 * 1024

export function ProfileSection() {
  const { t } = useTranslation('accountPages')
  const { data, isLoading, isError } = useProfile()
  const updateProfile = useUpdateProfile()
  const uploadAvatar = useUploadAvatar()
  const deleteAvatar = useDeleteAvatar()

  const setUser = useAuthStore((s) => s.setUser)
  const user = useAuthStore((s) => s.user)
  const presenceMap = usePresenceMap()
  const setPresence = useSetMyPresence()

  const [fullName, setFullName] = useState('')
  const [pwOpen, setPwOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (data) setFullName(data.fullName)
  }, [data])

  // Đồng bộ lại authStore (giữ nguyên các field khác của AuthUser).
  function syncAuth(p: Profile) {
    setUser({
      ...(user ?? {}),
      id: p.id,
      email: p.email,
      fullName: p.fullName,
      role: p.role,
      avatarUrl: p.avatarUrl,
      orgId: user?.orgId ?? '',
    })
  }

  if (isLoading) return <Loading label={t('profile.loading')} />
  if (isError || !data) return <ErrorState />

  const nameDirty = fullName.trim() !== data.fullName

  async function onSaveName(e: React.FormEvent) {
    e.preventDefault()
    if (!fullName.trim()) {
      toast.error(t('profile.nameRequired'))
      return
    }
    try {
      const updated = await updateProfile.mutateAsync({ fullName: fullName.trim() })
      syncAuth(updated)
      toast.success(t('profile.nameUpdated'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // cho phép chọn lại cùng file
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.pickImageFile'))
      return
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error(t('profile.avatarTooLarge'))
      return
    }
    try {
      const updated = await uploadAvatar.mutateAsync(file)
      syncAuth(updated)
      toast.success(t('profile.avatarUpdated'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onRemoveAvatar() {
    try {
      const updated = await deleteAvatar.mutateAsync()
      syncAuth(updated)
      toast.success(t('profile.avatarRemoved'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const uploading = uploadAvatar.isPending || deleteAvatar.isPending

  return (
    <div className="max-w-3xl space-y-6">
      {/* Thẻ nhận diện: ảnh lớn + tên + email */}
      <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:p-5">
        <div className="relative w-fit">
          <Avatar className="h-20 w-20">
            {data.avatarUrl && <AvatarImage src={avatarSrc(data.avatarUrl)} />}
            <AvatarFallback className="text-lg">{initials(data.fullName)}</AvatarFallback>
          </Avatar>
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            aria-label={t('profile.changeAvatar')}
            title={t('profile.changeAvatar')}
            className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm hover:text-primary disabled:opacity-60"
          >
            {uploadAvatar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-medium">{data.fullName}</p>
          <p className="truncate text-sm text-muted-foreground">{data.email}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{data.roleName || ROLE_LABELS[data.role]}</Badge>
            {user?.org?.name && <span className="truncate text-xs text-muted-foreground">{user.org.name}</span>}
          </div>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          {data.avatarUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={uploading}
              onClick={onRemoveAvatar}
            >
              {deleteAvatar.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
              {t('profile.removeAvatar')}
            </Button>
          )}
          <p className="text-xs text-muted-foreground">{t('profile.avatarHint')}</p>
        </div>
      </div>

      <AccountSection title={t('profile.sectionAccount')}>
        <AccountRow label={t('profile.fullNameLabel')} hint={t('profile.fullNameHint')}>
          <form onSubmit={onSaveName} className="flex max-w-md gap-2">
            <Input
              aria-label={t('profile.fullNameLabel')}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('profile.fullNamePlaceholder')}
            />
            <Button type="submit" disabled={!nameDirty || updateProfile.isPending}>
              {updateProfile.isPending && <Loader2 className="animate-spin" />}
              {t('profile.save')}
            </Button>
          </form>
        </AccountRow>
        <AccountRow label={t('profile.emailLabel')} hint={t('profile.emailHint')}>
          <Input aria-label={t('profile.emailLabel')} value={data.email} readOnly disabled className="max-w-md" />
        </AccountRow>
        <AccountRow label={t('profile.roleLabel')} hint={t('profile.roleHint')}>
          <Badge variant="secondary">{data.roleName || ROLE_LABELS[data.role]}</Badge>
        </AccountRow>
        <AccountRow
          label={t('profile.presenceLabel')}
          hint={t('profile.presenceHint')}
        >
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('profile.presenceLabel')}>
            {PRESENCE_OPTIONS.map((o) => {
              const active = presenceOf(presenceMap.data, data.id) === o.id
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  title={o.hint}
                  disabled={setPresence.isPending || !!user?.impersonatedBy}
                  onClick={() => {
                    if (active) return
                    setPresence.mutate(o.id, {
                      onSuccess: () => toast.success(t('profile.presenceChanged', { status: o.label })),
                      onError: (err) => toast.error(apiError(err)),
                    })
                  }}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-60',
                    active ? 'border-primary bg-primary/5 text-primary' : 'hover:bg-accent',
                  )}
                >
                  <span className={cn('h-2 w-2 rounded-full', PRESENCE_DOT[o.id])} />
                  {o.label}
                </button>
              )
            })}
          </div>
        </AccountRow>
      </AccountSection>

      <AccountSection title={t('profile.sectionSecurity')}>
        <AccountRow label={t('profile.passwordLabel')} hint={t('profile.passwordHint')}>
          <Button type="button" variant="outline" onClick={() => setPwOpen(true)}>
            {t('changePassword.title')}
          </Button>
        </AccountRow>
        <TotpSettingsSection />
      </AccountSection>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  )
}
